import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Avatar del sistema de diseño: la foto si existe, y si no las iniciales sobre un fondo
 * azul claro (así se ve el bloque de usuario en la referencia visual).
 */

type AvatarSize = 'sm' | 'md' | 'lg';

const sizeStyles: Record<AvatarSize, string> = {
  sm: 'size-8 text-micro',
  md: 'size-9 text-caption',
  lg: 'size-11 text-body',
};

function initialsFrom(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export interface AvatarProps {
  /** Nombre o email, para las iniciales y el `alt`. */
  name: string;
  /** URL de la imagen (logo del tenant, foto del usuario). */
  src?: string | null;
  size?: AvatarSize;
  className?: string;
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill border border-line bg-brand-50 font-semibold text-brand-700',
        sizeStyles[size],
        className,
      )}
      aria-hidden={showImage ? undefined : 'true'}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt={name}
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initialsFrom(name)
      )}
    </span>
  );
}
